import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { automationApi, automationSettingsQuery, queueCountQuery } from "@web/lib/queries-automation";
import { useMe } from "@web/lib/session";
import {
  BottomSheet, Button, Card, FormGrid, Inline, Page, Region, Row, RowGroup, Skeleton, Stack,
  StatusChip, Text, TextField, Toggle, UnsavedChanges, Workspace, useToast,
} from "@web/ui";
import { AdminsOnly } from "./Settings";

/** Settings › Automation (SPEC §6.1 item 13): the one switch that decides whether
 * automated messages wait for a person, the quiet window they respect, and the
 * reminder ladders — shown, but read-only: they are SPEC §7.14's, not a preference. */
export function SettingsAutomation() {
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const settings = useQuery({ ...automationSettingsQuery, enabled: me.companyAdmin });
  const queue = useQuery({ ...queueCountQuery, enabled: me.companyAdmin });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);

  if (!me.companyAdmin) return <AdminsOnly />;
  if (settings.isError) return <Page><Stack gap={3}><Text tone="danger">Automation settings could not be loaded.</Text><Button variant="secondary" onClick={() => void settings.refetch()}>Try again</Button></Stack></Page>;
  if (!settings.data) return <Page><Skeleton count={4} /></Page>;

  const data = settings.data;
  const quietStart = start ?? data.quietHours.start;
  const quietEnd = end ?? data.quietHours.end;
  const quietDirty = quietStart !== data.quietHours.start || quietEnd !== data.quietHours.end;

  async function save(patch: Parameters<typeof automationApi.save>[0], done: string) {
    setBusy(true);
    try { await automationApi.save(patch); toast.success(done); setStart(null); setEnd(null); }
    catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  return <Page>
    <UnsavedChanges dirty={quietDirty} pending={busy} />
    <Workspace variant="form"><Region area="main"><Stack gap={4}>
      <Card><Stack gap={3}>
        <Inline justify="between" align="start" wrap>
          <Stack gap={1}>
            <Text variant="title">Send automated messages without review</Text>
            <Text variant="caption" tone="secondary">
              {data.autoSendAutomated
                ? "Automated messages go straight out. Nothing waits in the queue."
                : "Everything the automation writes waits in the queue until somebody releases it."}
            </Text>
          </Stack>
          <StatusChip tone={data.autoSendAutomated ? "warn" : "success"}>{data.autoSendAutomated ? "Sending automatically" : "Reviewed by a person"}</StatusChip>
        </Inline>
        <Toggle label="Send automatically" checked={data.autoSendAutomated} disabled={busy}
          onChange={value => value ? setConfirming(true) : void save({ autoSendAutomated: false }, "Automated messages will wait for review")} />
        {!data.autoSendAutomated && (queue.data ?? 0) > 0 && <Row title={`${queue.data} waiting in the queue`} subtitle="Read them before turning this on" onClick={() => navigate({ to: "/more/queue" })} />}
      </Stack></Card>

      <Card><Stack gap={3}>
        <Stack gap={1}>
          <Text variant="title">Quiet hours</Text>
          <Text variant="caption" tone="secondary">Automated messages wait until the window opens. Times are in your own timezone, and a message is never dropped — only delayed.</Text>
        </Stack>
        <FormGrid>
          <TextField label="Stop sending at" type="time" value={quietStart} onChange={event => setStart(event.target.value)} />
          <TextField label="Start again at" type="time" value={quietEnd} onChange={event => setEnd(event.target.value)} />
        </FormGrid>
        <Button disabled={!quietDirty} loading={busy} onClick={() => void save({ quietHours: { start: quietStart, end: quietEnd } }, "Quiet hours saved")}>Save quiet hours</Button>
      </Stack></Card>

      <Card><Stack gap={3}>
        <Stack gap={1}>
          <Text variant="title">When reminders go out</Text>
          <Text variant="caption" tone="secondary">These are fixed. Change the words under Message templates instead.</Text>
        </Stack>
        <RowGroup>
          <Row title="Deposit and balance" subtitle={`${data.reminderDays.balance.map(day => `${day} days before`).join(" · ")} · then ${data.reminderDays.overdue.map(day => `${day} days late`).join(" · ")}`} wrap />
          <Row title="Insurance certificate" subtitle={data.reminderDays.insurance.map(day => `${day} days before`).join(" · ")} wrap />
          <Row title="Damage deposit · client" subtitle={data.reminderDays.damageClient.map(day => `${day} days before`).join(" · ")} wrap />
          <Row title="Damage deposit · team" subtitle={data.reminderDays.damageTeam.map(day => `${day} days before`).join(" · ")} wrap />
          <Row title="Tours" subtitle={`Client ${data.reminderDays.tourClientHours}h before · team ${data.reminderDays.tourStaffHours}h before`} wrap />
        </RowGroup>
      </Stack></Card>

      {!data.smsClientEnabled && <Card><Inline gap={2} align="start">
        <AlertTriangle aria-hidden size={20} />
        <Stack gap={1}>
          <Text variant="title">Client texting is off</Text>
          <Text variant="caption" tone="secondary">Automated texts reach staff phones only until the venue's own number is registered. Email automation is unaffected.</Text>
        </Stack>
      </Inline></Card>}
    </Stack></Region></Workspace>

    <BottomSheet open={confirming} onClose={() => setConfirming(false)} title="Send without review?"
      footer={<Button loading={busy} onClick={async () => { await save({ autoSendAutomated: true }, "Automated messages will send without review"); setConfirming(false); }}>Turn it on</Button>}>
      <Stack gap={3}>
        <Text>From now on, every message the automation writes goes to the client without anybody reading it first.</Text>
        <Text tone="secondary">That includes sales sequences, payment reminders, insurance chases and tour reminders. Quiet hours, consent and the one-text-a-day limit still apply.</Text>
        <Text tone="secondary">Most venues leave this off until they have watched the queue for a few weeks.</Text>
      </Stack>
    </BottomSheet>
  </Page>;
}
